// VERA-825 / VERA-246: Shipping section for the tag shop checkout card.
// Section header ("Shipping" + "Change" button) plus the selected address row
// (store icon + label + formatted line + edit pencil). Falls back to an
// "Add shipping address" affordance when the company has no saved addresses.

import React from 'react';

import {Stack, Typography, Box, useTheme} from '@mui/material';
import {Button, Skeleton} from '@repo/ui';
import {PlusIcon} from 'lucide-react';

import {ADDRESS_TYPE_LABELS} from 'types/Address';
import type {Address} from 'types';
import {getAddressIcon} from 'utils/addressIcons';

interface CheckoutShippingRowProps {
  address: Address | null;
  isLoading: boolean;
  onChange(): void;
  onAdd(): void;
  /** Section heading — defaults to "Shipping address"; billing passes "Billing address". */
  title?: string;
}

// One-line formatted address: "Seattle, WA 92949 US".
const formatAddressLine = (address: Address): string =>
  [
    [address.city, address.state].filter(Boolean).join(', '),
    address.postal_code,
    address.country,
  ]
    .filter(Boolean)
    .join(' ');

export const CheckoutShippingRow = ({
  address,
  isLoading,
  onChange,
  onAdd,
  title = 'Shipping address',
}: CheckoutShippingRowProps) => {
  const theme = useTheme();

  return (
    <Stack spacing={4}>
      <Typography variant="body1">{title}</Typography>

      {isLoading ? (
        // Neutral rectangle — at load we don't yet know if this becomes a saved
        // address row or an "Add" button, so don't mis-preview an avatar layout.
        <Skeleton height={40} />
      ) : address ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
        >
          <Stack direction="row" alignItems="center" spacing={3} minWidth={0}>
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.primary[100]}`,
                color: theme.palette.text.secondary,
              }}
            >
              {(() => {
                const Icon = getAddressIcon(address.type, address.icon);
                return <Icon size={20} strokeWidth={1} />;
              })()}
            </Box>
            <Stack minWidth={0}>
              <Typography variant="body2" noWrap>
                {address.label || ADDRESS_TYPE_LABELS[address.type]}
              </Typography>
              <Typography variant="body3" color="text.secondary" noWrap>
                {formatAddressLine(address)}
              </Typography>
            </Stack>
          </Stack>

          <Button
            variant="secondary"
            onClick={onChange}
            sx={{flexShrink: 0}}
          >
            Change
          </Button>
        </Stack>
      ) : (
        <Button
          variant="secondary"
          startIcon={<PlusIcon size={18} strokeWidth={1} />}
          onClick={onAdd}
          sx={{alignSelf: 'flex-start'}}
        >
          Add
        </Button>
      )}
    </Stack>
  );
};
